import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { InformeEnProceso } from "@/lib/informes";

interface ContactoDialogProps {
  /** El informe cuyo contacto se está mirando, o `null` con el diálogo cerrado. */
  informe: InformeEnProceso | null;
  onCerrar: () => void;
}

/**
 * Los datos de contacto del cliente de un informe, sin salir del listado.
 *
 * Existe porque el ejecutivo que mira la cola necesita llamar al cliente sin
 * perder los filtros y la página en los que está. Por eso es un diálogo y no
 * una pantalla.
 *
 * PENDIENTE del prototipo `18-08-2026`, declarado y no implementado: el aviso
 * de "sin respuesta hace N días" y el correo de recordatorio con asistencia de
 * IA. Los dos cuelgan de la fecha en que la cotización se envió al cliente,
 * que vive en `cotizacion` (Módulo F) y todavía no existe; la asistencia de IA
 * además está diferida por decisión de arquitectura (CLAUDE.md raíz). Dibujar
 * el aviso ahora sería mostrar "hace 0 días" en todas las filas.
 */
export function ContactoDialog({ informe, onCerrar }: ContactoDialogProps) {
  return (
    <Dialog open={informe !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="sm:max-w-[26.25rem]">
        <DialogHeader>
          <DialogDescription className="text-ink-hint text-xs font-bold tracking-wide uppercase">
            Datos de contacto
          </DialogDescription>
          <DialogTitle>{informe?.cliente_nombre ?? "Sin cliente"}</DialogTitle>
        </DialogHeader>

        <dl className="grid gap-3">
          <Dato etiqueta="Solicitante" valor={informe?.solicitante_nombre} />
          {/* Mono: es un dato duro que se lee dígito a dígito para marcarlo. */}
          <Dato etiqueta="Teléfono" valor={informe?.telefono_contacto} mono />
        </dl>

        <DialogFooter>
          <Button variant="soft" className="w-full" onClick={onCerrar}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Dato({
  etiqueta,
  valor,
  mono = false,
}: {
  etiqueta: string;
  valor: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <dt className="text-ink-hint text-[0.65625rem] font-bold tracking-wide uppercase">{etiqueta}</dt>
      {/* El guion largo distingue "el informe no tiene este dato" de un valor
          vacío que se lee como un renglón roto. Un borrador incompleto puede
          no tener ni solicitante ni teléfono. */}
      <dd className={`text-ink-body text-sm ${mono ? "font-mono" : ""}`}>{valor || "—"}</dd>
    </div>
  );
}
